/* global window */

import React, {Component} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import posed from 'react-pose';
import classnames from 'classnames';

import CheckboxGroup from '../CheckboxGroup';
import RadioGroup from '../RadioGroup';
import Button from '../Button';

import styles from './styles.module.css';

const easeIn = [0.4, 0.0, 1, 1];
const easeOut = [0.0, 0.0, 0.2, 1];

const baseDuration = 800;

const Options = posed.div({
    preEnter: {
        y: ({start}) => (start > 0 ? `${start}px` : '65vh'),
        opacity: ({start}) => (start > 0 ? 1 : 0),
    },
    enter: {
        y: 0,
        opacity: 1,
        transition: ({index}) => ({
            duration: baseDuration,
            ease: easeOut,
            delay: index === 0 ? 100 : 0,
        }),
        staggerChildren: 50,
        beforeChildren: true,
    },
    exit: {
        y: -75,
        opacity: 0,
        staggerChildren: 50,
        transition: {
            duration: baseDuration - 100,
            ease: easeIn,
        },
    },
});

const NextQuestion = posed.div({
    preEnter: {
        y: '100vh',
        opacity: 0,
    },
    enter: {
        y: 0,
        opacity: 1,
        transition: {
            duration: baseDuration,
            ease: easeOut,
        },
        delay: 200,
    },
    exit: {
        opacity: 0,
        y: ({nextStep}) => (nextStep ? 0 : '100vh'),
        transition: ({nextStep}) => ({
            duration: (nextStep ? 0 : baseDuration),
            ease: easeIn,
        }),
        delay: ({nextStep}) => (nextStep ? 100 : 0),
    },
});

const Question = posed.div({
    preEnter: {
        opacity: 0,
    },
    enter: {
        opacity: 1,
        transition: ({index}) => ({
            duration: baseDuration - 100,
            ease: easeOut,
            delay: index > 0 ? baseDuration : baseDuration - 100,
        }),
    },
    exit: {
        opacity: 0,
        transition: {
            duration: 300,
            ease: easeIn,
        },
    },
});

const Image = posed.div({
    preEnter: {
        y: '100%',
    },
    enter: {
        y: 0,
        transition: {
            duration: baseDuration - 200,
            ease: easeOut,
        },
    },
    exit: {
        y: '1000px',
        transition: ({nextStep}) => ({
            duration: nextStep ? 0 : baseDuration - 100,
            ease: easeIn,
            delay: nextStep ? 200 : 0,
        }),
    },
});

class Step extends Component {
    static propTypes = {
        // eslint-disable-next-line react/no-unused-prop-types
        index: PropTypes.number, // Used by react-pose for delays
        type: PropTypes.string.isRequired,
        question: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        image: PropTypes.string.isRequired,
        optionsText: PropTypes.string.isRequired,
        options: PropTypes.oneOfType([
            PropTypes.arrayOf(PropTypes.object),
            PropTypes.object,
        ]),
        initialResult: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
        onNextQuestionPos: PropTypes.func.isRequired,
        nextQuestionPos: PropTypes.number.isRequired,
        onComplete: PropTypes.func.isRequired,
        onBack: PropTypes.func.isRequired,
        hostRef: PropTypes.func.isRequired,
    };

    static defaultProps = {
        index: 0,
        options: {},
        initialResult: null,
    };

    constructor(props) {
        super(props);

        const {initialResult, type} = props;

        const defaultResult = type === 'multiselect' ? [] : null;
        this.state = {
            result: initialResult || defaultResult,
            imageHeight: 0,
        };
    }

    componentDidMount() {
        if(this.image) {
            const image = ReactDOM.findDOMNode(this.image); // eslint-disable-line
            this.setState({
                imageHeight: image.getBoundingClientRect().height,
            });
        }
    }

    onComplete = () => {
        const {onComplete} = this.props;
        const {result} = this.state;

        const {onNextQuestionPos} = this.props;
        const nextQuestion = ReactDOM.findDOMNode(this.nextQuestion); // eslint-disable-line
        const options = ReactDOM.findDOMNode(this.options); // eslint-disable-line
        const questionRect = nextQuestion.getBoundingClientRect();
        const optionsRect = options.getBoundingClientRect();
        const optionsPaddingTop = parseFloat(window.getComputedStyle(options).paddingTop);
        const y = questionRect.y - optionsRect.y - optionsPaddingTop;

        onNextQuestionPos(y, () => onComplete(result));
    }

    toggleMulti = (value) => {
        const {result} = this.state;

        const idx = result.findIndex((opt) => opt.value === value);
        if(idx > -1) {
            const newResult = [...result];
            newResult.splice(idx, 1);
            this.setState({result: newResult});
        } else {
            const {options} = this.props;
            const option = options.find((opt) => opt.value === value);
            this.setState({result: [...result, option]});
        }
    }

    selectNumeric = (value) => {
        this.setState({result: value});
    }

    selectOne = (value) => {
        const {options} = this.props;
        const option = options.find((opt) => opt.value === value);
        this.setState({result: option});
    }

    renderNext() {
        const {result} = this.state;

        return (
            <Button
                className={styles.next}
                disabled={result === null || result.length === 0}
                onClick={this.onComplete}
                onKeyDown={
                    (evt) => (evt.keyCode === 13 || evt.keyCode === 32)
                        && this.onComplete()
                }
            >
                <svg viewBox="0 0 37 32" xmlns="http://www.w3.org/2000/svg">
                    <g fillRule="nonzero">
                        <path d="M20.608 1.068L21.683.022l15.31 15.723-15.31 15.723-1.075-1.046 14.29-14.677z" />
                        <path d="M35.4 15v1.5H.2V15z" />
                    </g>
                </svg>
            </Button>
        );
    }

    renderBack() {
        const {onBack, index} = this.props;

        return (
            <Button
                className={styles.back}
                disabled={index === 0}
                onClick={onBack}
                onKeyDown={
                    (evt) => (evt.keyCode === 13 || evt.keyCode === 32)
                        && onBack()
                }
            >
                <svg viewBox="0 0 37 32" xmlns="http://www.w3.org/2000/svg">
                    <g fillRule="nonzero">
                        <path d="M20.608 1.068L21.683.022l15.31 15.723-15.31 15.723-1.075-1.046 14.29-14.677z" />
                        <path d="M35.4 15v1.5H.2V15z" />
                    </g>
                </svg>
            </Button>
        );
    }

    render() {
        const {
            type, question, description, options, hostRef,
            index, nextQuestionPos, optionsText, image,
        } = this.props;
        const {result, imageHeight} = this.state;

        let control;
        let range;

        switch (type) {
        case 'multiselect':
            control = (
                <CheckboxGroup
                    onSelect={this.toggleMulti}
                    options={options}
                    value={result ? result.map((opt) => opt.value) : null}
                />
            );
            break;

        case 'numeric':
            range = [];
            for(let i = (options.min || 1); i <= (options.max || 8); i += 1)
                range.push(i);

            control = (
                <RadioGroup
                    type="number"
                    options={range}
                    value={result}
                    onSelect={this.selectNumeric}
                />
            );
            break;

        case 'detailed':
            control = (
                <RadioGroup
                    type="box"
                    options={options}
                    value={result && result.value}
                    onSelect={this.selectOne}
                />
            );
            break;

        default:
            control = (
                <div>Invalid control specified.</div>
            );
        }

        return (
            <div className={styles.container} ref={hostRef}>
                <div className={styles.questionWrap}>
                    <Question className={styles.question} index={index} key="question">
                        <h2>{question}</h2>
                        {description && (<p>{description}</p>)}
                    </Question>
                    <div
                        key="image"
                        className={styles.imageWrap}
                        ref={(imageEl) => {
                            this.image = imageEl;
                        }}
                    >
                        <Image
                            className={styles.image}
                            style={{
                                backgroundImage: `url(${image})`,
                            }}
                        />
                    </div>
                </div>
                <div className={styles.optionsWrap}>
                    <Options
                        index={index}
                        start={nextQuestionPos}
                        className={classnames(styles.options, styles[type])}
                        key="options"
                    >
                        <div
                            className={styles.optionsHeader}
                            ref={(el) => {
                                this.options = el;
                            }}
                        >
                            <span className={styles.optionsText}>
                                {optionsText}
                                <span>
                                    (
                                    {range ? range.length : options.length}
                                    )
                                </span>
                            </span>
                        </div>
                        <div className={styles.control}>{control}</div>
                    </Options>
                    <NextQuestion
                        index={index}
                        className={classnames(styles.options, styles.nextStep)}
                        key="nextQuestion"
                    >
                        <div className={styles.title} />
                        <div
                            className={styles.control}
                            ref={(el) => {
                                this.nextQuestion = el;
                            }}
                        />
                    </NextQuestion>
                </div>
                <Image
                    key="dots"
                    className={styles.dots}
                    style={{
                        height: `${imageHeight}px`,
                    }}
                />
                {this.renderBack()}
                {this.renderNext()}
            </div>
        );
    }
}

export default posed(Step)({
    preEnter: {
        opacity: 0,
    },
    enter: {
        opacity: 1,
        beforeChildren: true,
        transition: {
            duration: 0,
        },
        delay: ({index}) => (index === 0 ? 900 : 0),
        applyAtStart: {
            overflow: 'hidden',
        },
        applyAtEnd: {
            overflow: 'inherit',
        },
    },
    exit: {
        duration: baseDuration + 500,
    },
});
